import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Map, Navigation, Share2 } from 'lucide-react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import api from '../../../services/api'
import { fetchAddressByCep } from '../../../utils/shippingQuote'
import './AddressMapDropdown.css'

const PIN = L.divIcon({
  className: 'address-map-pin',
  html: '<span></span>',
  iconSize: [28, 28],
  iconAnchor: [14, 28]
})

function formatCep(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

function hasPoint(value) {
  return Number.isFinite(Number(value?.latitude)) && Number.isFinite(Number(value?.longitude))
}

export function addressRouteUrl(value) {
  if (hasPoint(value)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${Number(value.latitude)},${Number(value.longitude)}`
  }
  if (value?.address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(value.address)}`
  }
  return ''
}

function AddressMapDropdown({ value, onChange, readOnly = false, shareTitle = 'Endereço', defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const [cep, setCep] = useState(formatCep(value?.cep))
  const [lookingUp, setLookingUp] = useState(false)
  const [notice, setNotice] = useState('')
  const hostRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  valueRef.current = value
  onChangeRef.current = onChange
  const latitude = value?.latitude
  const longitude = value?.longitude

  useEffect(() => {
    setCep(formatCep(value?.cep))
  }, [value?.cep])

  useEffect(() => {
    if (!open || !hostRef.current) return undefined
    const current = valueRef.current
    const start = hasPoint(current)
      ? [Number(current.latitude), Number(current.longitude)]
      : [-14.235, -51.9253]
    const map = L.map(hostRef.current, { zoomControl: true, attributionControl: false })
    map.setView(start, hasPoint(current) ? 16 : 4)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(map)
    mapRef.current = map

    const movePin = (latitude, longitude, emit) => {
      const point = [latitude, longitude]
      if (!markerRef.current) {
        markerRef.current = L.marker(point, { icon: PIN, draggable: !readOnly }).addTo(map)
        if (!readOnly) {
          markerRef.current.on('dragend', () => {
            const position = markerRef.current.getLatLng()
            onChangeRef.current({
              ...valueRef.current,
              latitude: Number(position.lat.toFixed(6)),
              longitude: Number(position.lng.toFixed(6))
            })
          })
        }
      } else {
        markerRef.current.setLatLng(point)
      }
      if (emit) {
        onChangeRef.current({
          ...valueRef.current,
          latitude: Number(latitude.toFixed(6)),
          longitude: Number(longitude.toFixed(6))
        })
      }
    }

    if (hasPoint(current)) movePin(Number(current.latitude), Number(current.longitude), false)
    if (!readOnly) {
      map.on('click', (event) => movePin(event.latlng.lat, event.latlng.lng, true))
    }

    const timer = window.setTimeout(() => map.invalidateSize(), 80)
    return () => {
      window.clearTimeout(timer)
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
  }, [open, readOnly])

  useEffect(() => {
    if (!open || !mapRef.current || !hasPoint({ latitude, longitude })) return
    const point = [Number(latitude), Number(longitude)]
    if (!markerRef.current) {
      markerRef.current = L.marker(point, { icon: PIN, draggable: !readOnly }).addTo(mapRef.current)
      if (!readOnly) {
        markerRef.current.on('dragend', () => {
          const position = markerRef.current.getLatLng()
          onChangeRef.current({
            ...valueRef.current,
            latitude: Number(position.lat.toFixed(6)),
            longitude: Number(position.lng.toFixed(6))
          })
        })
      }
    } else {
      markerRef.current.setLatLng(point)
    }
    mapRef.current.setView(point, Math.max(mapRef.current.getZoom(), 16))
  }, [open, readOnly, latitude, longitude])

  const lookupCep = async () => {
    setLookingUp(true)
    setNotice('')
    try {
      const info = await fetchAddressByCep(cep)
      const line = [info?.street, info?.neighborhood, info?.city, info?.regionCode].filter(Boolean).join(', ')
      const query = [line || value.address, cep, 'Brasil'].filter(Boolean).join(', ')
      let point = null
      try {
        const response = await api.get('/api/geo/search', { params: { q: query } })
        point = response.data?.data
      } catch (error) {
        if (error.response?.status !== 404) throw error
      }
      onChange({
        ...value,
        cep: cep.replace(/\D/g, ''),
        address: value.address?.trim() ? value.address : line,
        latitude: point?.latitude ?? value.latitude,
        longitude: point?.longitude ?? value.longitude
      })
      setOpen(true)
      if (!point) setNotice('CEP encontrado, mas o mapa não marcou a casa. Clique no mapa para pinar.')
    } catch (error) {
      setNotice(error.response?.data?.message || error.message || 'Não foi possível buscar o CEP')
    } finally {
      setLookingUp(false)
    }
  }

  const route = addressRouteUrl(value)
  const shareText = [shareTitle, value.address, formatCep(value.cep), route].filter(Boolean).join('\n')

  const share = async () => {
    if (!route && !value.address) return
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: route || undefined })
        return
      } catch (error) {
        if (error?.name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(shareText)
      setNotice('Link da rota copiado.')
    } catch {
      setNotice('Não foi possível copiar o link.')
    }
  }

  return (
    <div className={`address-map${open ? ' is-open' : ''}`}>
      <button type="button" className="address-map-toggle" onClick={() => setOpen((current) => !current)} aria-expanded={open}>
        <Map size={16} />
        <span>
          <strong>{value.address || 'Endereço não informado'}</strong>
          <small>
            {formatCep(value.cep) || 'Sem CEP'}
            {hasPoint(value) ? ' · Casa marcada no mapa' : ''}
          </small>
        </span>
        <ChevronDown size={16} className="address-map-chevron" />
      </button>
      <div className="address-map-share">
        <a className={`address-map-btn address-map-btn--primary${route ? '' : ' is-disabled'}`} href={route || undefined} target="_blank" rel="noreferrer" aria-disabled={!route} onClick={(event) => { if (!route) event.preventDefault() }}>
          <Navigation size={14} /> Abrir rota
        </a>
        <button type="button" className="address-map-btn" onClick={share} disabled={!route && !value.address}>
          <Share2 size={14} /> Compartilhar
        </button>
        {route && (
          <a className="address-map-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer">
            WhatsApp
          </a>
        )}
      </div>
      {open && (
        <div className="address-map-panel">
          <div className="address-map-fields">
            <label>
              <span>CEP</span>
              <input
                value={cep}
                onChange={(event) => setCep(formatCep(event.target.value))}
                inputMode="numeric"
                placeholder="00000-000"
                disabled={readOnly}
              />
            </label>
            {!readOnly && (
              <button type="button" className="address-map-btn" onClick={lookupCep} disabled={lookingUp || cep.replace(/\D/g, '').length !== 8}>
                {lookingUp ? 'Buscando...' : 'Buscar CEP'}
              </button>
            )}
          </div>
          <label className="address-map-address">
            <span>Endereço</span>
            <textarea
              rows={2}
              value={value.address || ''}
              placeholder="Rua, número, bairro, cidade"
              disabled={readOnly}
              onChange={(event) => onChange({ ...value, address: event.target.value })}
            />
          </label>
          <div ref={hostRef} className="address-map-canvas" />
          {!readOnly && <p className="address-map-hint">Clique no mapa ou arraste o pino para marcar a casa.</p>}
          {notice && <p className="address-map-notice">{notice}</p>}
        </div>
      )}
    </div>
  )
}

export default AddressMapDropdown
